import { useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import { FormattedMessage } from 'react-intl'
import { toast } from 'sonner'

import { queryKeys } from '@/constants/queryKeys'
import { BankEligibility } from '@/domains/Business/types'
import { useErrorToast } from '@/hooks/useErrorToast'
import { useKeyPress } from '@/hooks/useKeyPress'
import { getFullAddress } from '@/lib/address'
import { queryClient } from '@/lib/queryClient'
import { getFullName } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { GoBackButton, UploadedFile } from '@/shared/components'
import { Reload } from '@/shared/icons/outline'
import { Download } from '@/shared/icons/solid'
import {
  Button,
  Card,
  Label,
  MotionDiv,
  SlideInScreen,
  Spinner,
  StickyContainer,
  Typography,
} from '@/shared/ui'

import { deleteProofOfAddress, uploadProofOfAddress } from '../api'

type Props = {
  bankEligibility?: BankEligibility
  ownerId?: string
  onBack: () => void
  isPending: boolean
}

export const ProofOfAddressDocumentsScreen = ({
  bankEligibility,
  onBack,
  ownerId,
}: Props) => {
  const notifyError = useErrorToast()

  const { mutateAsync: uploadDocument, isPending: isUploading } = useMutation({
    mutationFn: uploadProofOfAddress,
  })

  const { mutateAsync: deleteDocument, isPending: isDeleting } = useMutation({
    mutationFn: deleteProofOfAddress,
  })

  const beneficialOwner = useMemo(
    () =>
      bankEligibility?.currentStepDetails.data.beneficialOwners.find(
        (owner) => owner.id === ownerId,
      ),
    [bankEligibility, ownerId],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'application/pdf': [],
      'image/jpg': [],
    },
    multiple: false,
    disabled: isUploading || isDeleting,
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles.length || !beneficialOwner?.id) {
        return
      }

      const file = acceptedFiles[0]

      if (
        !bankEligibility?.currentStepDetails.data.businessId ||
        !bankEligibility?.currentStepDetails.step
      ) {
        toast.error('Missing businessId or step info')
        return
      }

      try {
        if (beneficialOwner?.document?.fileName) {
          await deleteDocument({
            documentName: beneficialOwner.document.fileName,
            businessId: bankEligibility.currentStepDetails.data.businessId,
            beneficialOwnerId: beneficialOwner.id,
            step: bankEligibility.currentStepDetails.step,
          })
        }

        const response = await uploadDocument({
          file,
          businessId: bankEligibility.currentStepDetails.data.businessId,
          beneficialOwnerId: beneficialOwner.id,
          step: bankEligibility.currentStepDetails.step,
        })

        queryClient.setQueryData([queryKeys.getBankEligibility], response)
      } catch (error) {
        notifyError(error)
      }
    },
  })

  useKeyPress('Enter', onBack)

  const handleSelectClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    open()
  }

  const handleReplaceFile = async () => {
    if (!beneficialOwner?.document?.fileName || !beneficialOwner?.id) {
      return
    }

    if (
      !bankEligibility?.currentStepDetails.data.businessId ||
      !bankEligibility?.currentStepDetails.step
    ) {
      toast.error('Missing businessId or step info')
      return
    }

    try {
      await deleteDocument({
        documentName: beneficialOwner.document.fileName,
        businessId: bankEligibility.currentStepDetails.data.businessId,
        beneficialOwnerId: beneficialOwner.id,
        step: bankEligibility.currentStepDetails.step,
      })

      queryClient.refetchQueries({
        queryKey: [queryKeys.getBankEligibility],
      })
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <>
      <GoBackButton onClick={onBack} />

      <SlideInScreen>
        <Typography text="center" variant="h3">
          <FormattedMessage
            defaultMessage="Proof of address for {ownerName}"
            id="kyb.proofOfAddress.documents.title"
            values={{ ownerName: getFullName(beneficialOwner) }}
          />
        </Typography>
        <div className="p-2" />
        <Typography className="text-center">
          <FormattedMessage
            defaultMessage="Help us validate {name}'s address by providing a utility bill or personal bank statement that shows it"
            id="kyb.proofOfAddress.documents.subtitle"
            values={{ name: beneficialOwner?.firstName }}
          />
        </Typography>
        <div className="p-6" />

        <Card size="upload">
          <div className="flex flex-col gap-2">
            <Typography bold>
              <FormattedMessage
                id="kyb.proofOfAddress.documents.uploadCSV.title"
                defaultMessage="Proof of residential address"
              />
            </Typography>
            <Typography className="text-neutral-gray-600">
              {getFullAddress(beneficialOwner?.address)}
            </Typography>
          </div>

          <div className="p-2" />
          <div className={cn('flex w-full gap-6')} {...getRootProps()}>
            <input
              {...getInputProps({ id: 'kyb-document', name: 'kyb-document' })}
            />

            <Label className="sr-only" htmlFor="kyb-document">
              <FormattedMessage
                id="uploadFileField.dragOrClick"
                defaultMessage="Drag and drop or click to upload"
              />
            </Label>

            <MotionDiv
              key={beneficialOwner?.document?.fileName}
              className="w-full"
            >
              {beneficialOwner?.document?.fileName ? (
                <div className="flex w-full flex-col justify-center gap-2">
                  <UploadedFile
                    key={beneficialOwner?.document?.fileName}
                    file={beneficialOwner?.document?.displayableName}
                    onClick={() => {
                      handleReplaceFile()
                    }}
                    isPending={isDeleting}
                  />

                  <div className="p-1" />

                  <Button
                    disabled={isUploading || isDeleting}
                    loading={isUploading}
                    loaderPosition="left"
                    onClick={handleSelectClick}
                    leftIcon={<Reload className="size-4" />}
                    type="button"
                    variant="secondary"
                  >
                    <FormattedMessage
                      id="uploadFile.field.replace"
                      defaultMessage="Replace file"
                    />
                  </Button>
                </div>
              ) : (
                <div
                  className={cn(
                    'bg-neutral-gray-100 hover:bg-neutral-gray-200 flex h-[84px] w-full cursor-pointer items-center justify-center rounded-lg p-3 transition-all',
                    {
                      'border-neutral-gray-600 border border-dashed':
                        isDragActive || !!beneficialOwner?.document,
                      'bg-neutral-gray-200': isDragActive,
                    },
                  )}
                >
                  <div className="text-neutral-gray-800 flex items-center gap-2">
                    {isUploading ? (
                      <Spinner className="size-5" />
                    ) : (
                      <Download className="size-5" />
                    )}
                    <Typography>
                      <FormattedMessage
                        id="uploadFileField.dragOrClick"
                        defaultMessage="Drag and drop or click to upload"
                      />
                    </Typography>
                  </div>
                </div>
              )}
            </MotionDiv>
          </div>
        </Card>

        <StickyContainer>
          <Button
            width="full"
            disabled={!beneficialOwner?.document || isUploading}
            onClick={onBack}
            type="submit"
          >
            <FormattedMessage
              defaultMessage="Save & Continue"
              id="action.saveAndContinue"
            />
          </Button>
        </StickyContainer>
      </SlideInScreen>
    </>
  )
}
